/**
 * markdown.js — Markdown → HTML for bot bubbles.
 *
 * Base (all tenants): goosecross convertMarkdownToHtml + formatHtml
 *   - vendor/marked.min.js (marked@14.1.3 / markedd.js)
 *   - GFM + breaks, hr AFTER each h3, link targets
 *   - **Label:** lines → markdown list items (✦ via shared CSS)
 *
 * Fogarty only (old thomasfogarty):
 *   - hr BEFORE each h3 (line above the title — Splash ss)
 *   - promote leading `[Title](url)` to <h3> when ### is missing
 *   - class bare --- hrs; CSS uses • bullets (not ✦)
 *
 * USAGE:
 *   import { renderInlineMarkdown, escapeHtml } from "../services/markdown.js";
 *   bubble.innerHTML = renderInlineMarkdown(text);
 */

// UMD build — assigns `globalThis.marked` (no ESM exports).
import "../../vendor/marked.min.js";
import { getTenantId } from "../core/configAccessors.js";

export function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function getMarked() {
  const m = globalThis.marked;
  if (m && typeof m.parse === "function") return m;
  return null;
}

function isFogartyTenant() {
  const id = String(getTenantId() || "").toLowerCase();
  return id === "fogartywinery" || id === "thomasfogarty";
}

function formatHtml(root) {
  root.querySelectorAll("p").forEach((p) => {
    p.innerHTML = p.innerHTML
      .replace(/__NEWLINE__{2,}/g, "<br/><br/>")
      .replace(/__NEWLINE__/g, "<br/>");
  });

  root.querySelectorAll("ul, ol").forEach((list) => {
    list.style.marginLeft = "0";
  });

  root.querySelectorAll("h1, h2, h3, h4, h5, h6").forEach((heading) => {
    heading.style.marginTop = "0.5em";
    heading.style.marginBottom = "0";
    heading.style.marginLeft = "0.5em";
  });
}

function isTitleLinkParagraph(el) {
  if (!el || el.tagName !== "P") return false;
  const kids = [...el.childNodes].filter(
    (n) => !(n.nodeType === 3 && !String(n.textContent).trim()),
  );
  if (kids.length !== 1) return false;
  const only = kids[0];
  if (only.nodeType === 1 && only.tagName === "A") return true;
  if (
    only.nodeType === 1 &&
    only.tagName === "STRONG" &&
    only.childNodes.length === 1 &&
    only.firstElementChild?.tagName === "A"
  ) {
    return true;
  }
  return false;
}

/** Fogarty: `[Title](url)` lead-in → <h3> when LLM skips ###. */
function promoteLeadingTitleLink(root) {
  const first = root.firstElementChild;
  if (!first || /^H[1-6]$/.test(first.tagName)) return;
  if (!isTitleLinkParagraph(first)) return;
  const h3 = root.ownerDocument.createElement("h3");
  h3.innerHTML = first.innerHTML;
  root.replaceChild(h3, first);
}

/**
 * @param {"before"|"after"} placement
 *   Fogarty old = before (line above title); goosecross = after.
 */
function insertHrsForHeadings(root, placement) {
  root.querySelectorAll("h3").forEach((h3, index) => {
    const sibling =
      placement === "before" ? h3.previousElementSibling : h3.nextElementSibling;
    if (sibling && sibling.tagName === "HR") {
      sibling.className = "hr-after-h3";
      sibling.id = `hr-${index}`;
      return;
    }
    const hr = root.ownerDocument.createElement("hr");
    hr.className = "hr-after-h3";
    hr.id = `hr-${index}`;
    if (placement === "before") {
      h3.parentNode.insertBefore(hr, h3);
    } else {
      h3.parentNode.insertBefore(hr, h3.nextSibling);
    }
  });
}

function styleBareHrs(root) {
  root.querySelectorAll("hr").forEach((hr, index) => {
    if (!hr.classList.contains("hr-after-h3")) {
      hr.classList.add("hr-after-h3");
      if (!hr.id) hr.id = `hr-bare-${index}`;
    }
  });
}

/**
 * Wrap consecutive **Label:** paragraphs into <ul><li> so list bullets show
 * when the LLM omits markdown `- ` markers.
 */
function wrapAttributeParagraphsAsList(root) {
  const doc = root.ownerDocument;
  const attrHtmlRe =
    /^<strong>[^<]+?:<\/strong>\s*\S|^<strong>[^<]+?<\/strong>\s*:\s*\S/;

  const isAttrHtml = (html) => {
    const h = String(html || "").trim();
    if (!h || h.length > 280) return false;
    if (/<br\s*\/?>/i.test(h)) return false;
    return attrHtmlRe.test(h);
  };

  const isAttrPara = (el) => {
    if (!el || el.tagName !== "P") return false;
    const text = (el.textContent || "").trim();
    if (!text || text.length > 200) return false;
    return isAttrHtml(el.innerHTML);
  };

  [...root.querySelectorAll("p")].forEach((p) => {
    if (!p.querySelector("br")) return;
    const parts = p.innerHTML
      .split(/<br\s*\/?>/i)
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length < 2 || !parts.every(isAttrHtml)) return;
    const frag = doc.createDocumentFragment();
    parts.forEach((html) => {
      const np = doc.createElement("p");
      np.innerHTML = html;
      frag.appendChild(np);
    });
    p.replaceWith(frag);
  });

  let el = root.firstElementChild;
  while (el) {
    if (!isAttrPara(el)) {
      el = el.nextElementSibling;
      continue;
    }
    const group = [];
    let cur = el;
    while (isAttrPara(cur)) {
      group.push(cur);
      cur = cur.nextElementSibling;
    }
    const ul = doc.createElement("ul");
    group.forEach((p) => {
      const li = doc.createElement("li");
      li.innerHTML = p.innerHTML;
      ul.appendChild(li);
      p.remove();
    });
    if (cur) root.insertBefore(ul, cur);
    else root.appendChild(ul);
    el = cur;
  }
}

/** Prefixed `- ` onto **Label:** lines so marked emits real <ul><li>. */
function attributeLinesToMarkdownList(markdownText) {
  return markdownText
    .split("\n")
    .map((line) => {
      const trimmed = line.trim();
      if (!trimmed) return line;
      if (/^([-*+]|\d+\.)\s/.test(trimmed)) return line;
      if (/^#{1,6}\s/.test(trimmed)) return line;
      if (
        /^\*\*[^*\n]+?:\*\*\s+\S/.test(trimmed) ||
        /^\*\*[^*\n]+?\*\*\s*:\s*\S/.test(trimmed)
      ) {
        const indent = line.match(/^\s*/)[0];
        return `${indent}- ${trimmed}`;
      }
      return line;
    })
    .join("\n");
}

/**
 * @param {string} text
 * @param {string|null} [type] — "ongoing" marks <strong> with .note-heading
 */
export function renderInlineMarkdown(text, type = null) {
  if (text == null || text === "") return "";

  let markdownText = String(text).replace(/__NEWLINE__/g, "\n");
  markdownText = markdownText.replace(/(### )/g, "\n$1");
  markdownText = markdownText.replace(/(\n)(### )/g, "$1$2");
  markdownText = attributeLinesToMarkdownList(markdownText);

  const marked = getMarked();
  let htmlResponse;
  if (marked) {
    marked.setOptions({
      breaks: true,
      gfm: true,
    });
    htmlResponse = marked.parse(markdownText);
  } else {
    console.warn("[RedConcierge] marked.js unavailable — falling back to plain text");
    return escapeHtml(markdownText).replace(/\n/g, "<br>");
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlResponse, "text/html");

  doc.querySelectorAll("a").forEach((anchor) => {
    const href = anchor.getAttribute("href") || "";
    if (href.startsWith("ws-action:")) return;
    anchor.id = "links";
    anchor.setAttribute("target", "_blank");
    anchor.setAttribute("rel", "noopener noreferrer");
    anchor.style.cursor = "pointer";
  });

  const fogarty = isFogartyTenant();
  if (fogarty) {
    promoteLeadingTitleLink(doc.body);
    insertHrsForHeadings(doc.body, "before");
    styleBareHrs(doc.body);
  } else {
    insertHrsForHeadings(doc.body, "after");
  }

  wrapAttributeParagraphsAsList(doc.body);

  if (type === "ongoing") {
    doc.querySelectorAll("strong").forEach((el) => {
      el.classList.add("note-heading");
    });
  }

  formatHtml(doc.body);
  return doc.body.innerHTML;
}
